import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    inject,
    signal,
    untracked,
    viewChild,
    type ElementRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormField, form, required, type FieldTree } from '@angular/forms/signals';

import { Chat } from '@ai-sdk/angular';
import { getToolName, isToolUIPart, type FileUIPart } from 'ai';

import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import type { Id } from '@common/types';
import type { SitePageEntity } from '@common/types/site-page.type';
import { isUuid } from '@common/helpers';
import type { AiOperationResult } from '@sites/ai-connector';

import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { McpToolsService } from '@web/store/page-structure/ai-connector/tool-definitions/mcp-tools.service';
import { AiSessionService } from '@web/store/page-structure/ai-connector/ai-session.service';
import { SendpulseAiProviderService } from '@web/store/page-structure/ai-connector/provider/sendpulse-ai-provider.service';

import { SpNpsComponent } from '@libs/ui/sp-nps/nps-loader.component';

import { BaseDrawerComponent } from '../base-drawer.component';

import { AI_CHAT_MODEL, buildSystemPrompt } from './const/ai-chat-drawer.constant';
import { SendpulseChatTransport } from './service/sendpulse-chat-transport';
import type { SendpulseUIMessage } from './service/sendpulse-chat-transport';
import { ElementPickerService } from './service/element-picker.service';
import { formatPickedEntityReference } from './service/element-picker.util';
import { ScreenshotUploadError, ScreenshotUploadService } from './service/screenshot-upload.service';
import {
    extractImageReferenceUrls,
    formatUploadedImageReference,
    guessImageMediaType,
    imageFileFromClipboard,
} from './service/screenshot-upload.util';
import { EntityMentionService } from './service/entity-mention.service';
import { AiChatHistoryService, type AiChatHistoryTarget } from './service/ai-chat-history.service';
import {
    detectMentionQuery,
    filterEntityMentionCandidates,
    type EntityMentionCandidate,
    type MentionQuery,
} from './service/entity-mention.util';

const CHAT_PRODUCT = 'landings';

// TEMPORARY: set to a non-null string to short-circuit the network call and show that text as
// the assistant reply. NOTE: bypasses the whole model pipeline, including
// `textToolProtocolMiddleware` — pseudo-XML in mock text is stripped, not executed (that
// conversion is unit-tested in `extract-leaked-tool-calls.spec.ts` instead). Set back to `null`
// to call the real backend again.
const MOCK_RESPONSE_TEXT: string | null = null;

interface AiChatFormModel {
    userMessage: string;
}

@Component({
    selector: 'web-ai-chat-drawer',
    templateUrl: './ai-chat-drawer.component.html',
    styleUrls: ['./ai-chat-drawer.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, SpNpsComponent, TooltipModule, FormField, TranslateModule],
    providers: [AiChatHistoryService],
})
export class AiChatDrawerComponent extends BaseDrawerComponent {
    private readonly mcpToolsService: McpToolsService = inject(McpToolsService);
    private readonly aiSession: AiSessionService = inject(AiSessionService);
    private readonly sendpulseAiProviderService: SendpulseAiProviderService = inject(SendpulseAiProviderService);
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly elementPicker: ElementPickerService = inject(ElementPickerService);
    private readonly screenshotUpload: ScreenshotUploadService = inject(ScreenshotUploadService);
    private readonly entityMention: EntityMentionService = inject(EntityMentionService);
    private readonly history: AiChatHistoryService = inject(AiChatHistoryService);

    private readonly chatFormModel = signal<AiChatFormModel>({ userMessage: '' });

    public readonly chatForm: FieldTree<AiChatFormModel> = form(this.chatFormModel, (path) => {
        required(path.userMessage);
    });

    private readonly messageInputRef = viewChild<ElementRef<HTMLTextAreaElement>>('messageInput');

    /**
     * "Pick an element" toggle — the crosshair highlight itself is a CSS class `ElementPickerService`
     * toggles directly on the hovered canvas element, not anything this template renders (see that
     * service's own doc comment for why a `position: fixed` overlay div here specifically cannot work).
     */
    public readonly isPickingElement = this.elementPicker.active;

    public toggleElementPicker(): void {
        this.elementPicker.toggle();
        this.mentionQuery.set(null);
    }

    /**
     * Reacts to a completed pick rather than the toggle service inserting text itself: only this
     * component knows where the caret is and owns `chatForm`, and the picker has no template of its
     * own to read a `<textarea>` ref from.
     */
    private readonly insertPickedReferenceEffect = effect(() => {
        const entity = this.elementPicker.picked();

        if (!entity) {
            return;
        }

        this.insertReferenceAtCaret(formatPickedEntityReference(entity));
        this.elementPicker.clearPicked();
        this.mentionQuery.set(null);
    });

    /**
     * "@" autocomplete — typing "@" opens a dropdown of blocks/columns/widgets on the page, filtered
     * live as the query grows, producing the SAME `@type:id` reference the click-to-pick flow above
     * does (`formatPickedEntityReference` is shared between them).
     *
     * Positioned anchored to the textarea itself (`ai-chat-mention-dropdown`, `bottom: 100%` in the
     * `.less`) rather than tracked to the caret's exact pixel position — a plain `<textarea>` has no
     * API for that short of a "mirror div" measurement hack, and the simpler anchor is far more
     * robust across zoom levels/fonts for the size of win it buys here.
     */
    private readonly mentionQuery = signal<MentionQuery | null>(null);
    private readonly activeMentionIndex = signal(0);

    public readonly mentionCandidates = computed<EntityMentionCandidate[]>(() => {
        const query = this.mentionQuery();
        return query ? filterEntityMentionCandidates(this.entityMention.candidates(), query.query) : [];
    });

    public readonly activeMentionCandidate = computed(() => this.mentionCandidates()[this.activeMentionIndex()]);

    public onMessageInput(event: Event): void {
        const textarea = event.target as HTMLTextAreaElement;

        this.mentionQuery.set(detectMentionQuery(textarea.value, textarea.selectionStart ?? textarea.value.length));
        this.activeMentionIndex.set(0);
    }

    public selectMentionCandidate(candidate: EntityMentionCandidate): void {
        const query = this.mentionQuery();
        const textarea = this.messageInputRef()?.nativeElement;

        if (!query || !textarea) {
            return;
        }

        const current = this.chatForm.userMessage().value();
        const end = textarea.selectionStart ?? current.length;
        const insert = `${formatPickedEntityReference(candidate)} `;

        this.chatForm.userMessage().value.set(`${current.slice(0, query.start)}${insert}${current.slice(end)}`);
        this.mentionQuery.set(null);

        queueMicrotask(() => {
            const caret = query.start + insert.length;
            textarea.focus();
            textarea.setSelectionRange(caret, caret);
        });
    }

    public closeMentionDropdown(): void {
        this.mentionQuery.set(null);
    }

    private readonly screenshotInputRef = viewChild<ElementRef<HTMLInputElement>>('screenshotInput');

    /** Uploading state for the attach button + inline error for a failed screenshot upload. */
    public readonly isUploadingImage = signal(false);
    public readonly imageUploadErrorKey = signal<string | null>(null);

    /**
     * What is attached to the message being composed, as urls — kept HERE rather than as
     * `[image:<url>]` text in the textarea, so the user sees a thumbnail instead of a 100-character
     * CDN url in the middle of their own sentence. The reference text is re-appended to the message
     * at send time (`onSendMessage`), because the model still reads it: the system prompt's
     * `[image:<url>]` rule is what lets it pass an already-stored url into a widget's `data.url`,
     * and the file part that gives it VISION is inlined as base64 by the AI SDK, carrying no url.
     */
    private readonly attachedImages = signal<string[]>([]);

    public readonly attachedImageUrls = this.attachedImages.asReadonly();

    public removeAttachedImage(url: string): void {
        this.attachedImages.update((urls) => urls.filter((attached) => attached !== url));
    }

    public dismissImageUploadError(): void {
        this.imageUploadErrorKey.set(null);
    }

    /** Opens the OS file picker via the hidden `<input type="file">` — no custom drop zone needed. */
    public triggerScreenshotPicker(): void {
        this.screenshotInputRef()?.nativeElement.click();
    }

    public onScreenshotFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;

        input.value = '';

        if (file) {
            this.uploadAndAttachImage(file);
        }
    }

    /**
     * Only intercepts the paste when it actually carries an image — anything else (plain text, a
     * pasted link) falls through to the textarea's own default paste handling untouched.
     */
    public onMessagePaste(event: ClipboardEvent): void {
        const file = imageFileFromClipboard(event.clipboardData);

        if (!file) {
            return;
        }

        event.preventDefault();
        this.uploadAndAttachImage(file);
    }

    private async uploadAndAttachImage(file: File): Promise<void> {
        this.imageUploadErrorKey.set(null);
        this.isUploadingImage.set(true);

        try {
            const url = await this.screenshotUpload.upload(file);

            this.attachedImages.update((urls) => (urls.includes(url) ? urls : [...urls, url]));
        } catch (error) {
            this.imageUploadErrorKey.set(
                error instanceof ScreenshotUploadError ? error.message : 'ai_chat_attach_image_error_generic'
            );
        } finally {
            this.isUploadingImage.set(false);
            this.changeDetectorRef.detectChanges();
        }
    }

    private insertReferenceAtCaret(reference: string): void {
        const current = this.chatForm.userMessage().value();
        const textarea = this.messageInputRef()?.nativeElement;

        if (!textarea) {
            const separator = current && !current.endsWith(' ') ? ' ' : '';
            this.chatForm.userMessage().value.set(`${current}${separator}${reference} `);
            return;
        }

        const start = textarea.selectionStart ?? current.length;
        const end = textarea.selectionEnd ?? current.length;
        const insert = `${reference} `;

        this.chatForm.userMessage().value.set(`${current.slice(0, start)}${insert}${current.slice(end)}`);

        // The value write above re-renders the textarea's content on the next tick; the caret has
        // to be restored AFTER that, not in the same handler, or `setSelectionRange` lands on text
        // that hasn't updated yet.
        queueMicrotask(() => {
            const caret = start + insert.length;
            textarea.focus();
            textarea.setSelectionRange(caret, caret);
        });
    }

    /**
     * `Chat` owns messages/status/error state (was hand-rolled before: `aiChat` array,
     * `isLoadingAiRequest` boolean, manual try/catch). The transport is constructed once; the
     * remaining getter closures are read fresh on every send, so a flag can change without
     * rebuilding it.
     */
    public readonly chat = new Chat<SendpulseUIMessage>({
        transport: new SendpulseChatTransport({
            sendpulseAiProviderService: this.sendpulseAiProviderService,
            mcpToolsService: this.mcpToolsService,
            aiSession: this.aiSession,
            buildSystemPrompt,
            product: CHAT_PRODUCT,
            getSelectedModel: () => AI_CHAT_MODEL,
            getMockResponseText: () => MOCK_RESPONSE_TEXT,
        }),
        // After every reply, failed and stopped ones included: the chat keeps those turns too.
        onFinish: () => void this.history.saveNew(this.chat),
    });

    public readonly showDrawer$: Observable<boolean> = this.layoutFacade.showAiChatDrawer$;
    public readonly currentPage$: Observable<SitePageEntity> = this.pagesFacade.currentPage$;

    private readonly isOpen = toSignal(this.showDrawer$, { initialValue: false });
    private readonly siteId = toSignal(this.siteFacade.id$);
    private readonly pageId = toSignal(this.currentPage$.pipe(map((page) => page?.id)));

    /** A page not saved yet has no id the server knows, so no conversation to keep. */
    private readonly historyTarget = computed<AiChatHistoryTarget | null>(() => {
        const siteId = this.siteId();
        const pageId = this.pageId();

        return siteId != null && pageId != null && !isUuid(siteId) && !isUuid(pageId)
            ? { siteId: siteId as Id, pageId: pageId as Id }
            : null;
    });

    /** Loaded when the chat is opened on a page, not on every page switch behind a closed drawer. */
    private readonly loadHistoryEffect = effect(() => {
        if (!this.isOpen()) {
            return;
        }

        const target = this.historyTarget();

        untracked(() => void this.history.open(this.chat, target));
    });

    public readonly isLoading = computed(
        () => this.history.loading() || this.chat.status === 'submitted' || this.chat.status === 'streaming'
    );

    /**
     * While a reply is in flight the loading dots render INSIDE the assistant message being
     * streamed (it already exists from the first chunk on) — this standalone bubble only covers
     * the gap before that message is created, when the last message is still the user's.
     */
    public readonly showStandaloneLoader = computed(
        () => this.isLoading() && this.chat.messages.at(-1)?.role !== 'assistant'
    );

    public isStreamingMessage(message: SendpulseUIMessage, isLast: boolean): boolean {
        return isLast && message.role === 'assistant' && this.isLoading();
    }

    private readonly lastAssistantMetadata = computed(() => {
        const assistantMessages = this.chat.messages.filter((message) => message.role === 'assistant');
        return assistantMessages.at(-1)?.metadata;
    });

    public readonly creditsBalance = computed(() => this.lastAssistantMetadata()?.credits?.balance ?? null);
    public readonly creditsWarning = computed(() => this.lastAssistantMetadata()?.credits?.warning ?? null);

    /**
     * The failure text for the last send. Already translated: it is produced by
     * `SendpulseAiProviderService`, because `SendpulseChatTransport` flattens the thrown error to a
     * plain string on the way here (see that service's `authenticatedFetch`).
     *
     * Until this existed nothing rendered `chat.error` at all — a failed request looked exactly like
     * the assistant choosing not to answer.
     */
    public readonly errorMessage = computed(() => this.chat.error?.message ?? null);

    /** Clears the bubble so a retry starts clean; the failed turn's messages are kept. */
    public dismissError(): void {
        this.chat.clearError();
    }

    public async onSendMessage(): Promise<void> {
        const userMessage = this.chatForm.userMessage().value();
        const attached = this.attachedImages();

        // `required` on `userMessage` is the form's only rule, so this covers `invalid()` as well —
        // and additionally lets a message go out with nothing but attachments, which the old
        // text-only check could not express once the reference stopped living in the textarea.
        if ((!userMessage && !attached.length) || this.history.loading()) {
            return;
        }

        // The references the user never sees: appended to the OUTGOING text only, since the
        // thumbnail strip replaced them in the input. Still text, because that is the only form the
        // model can reuse — the file part below is inlined as base64 by the AI SDK and carries no
        // url with it.
        const text = [userMessage, ...attached.map(formatUploadedImageReference)].filter(Boolean).join(' ');

        this.chatForm.userMessage().value.set('');
        this.attachedImages.set([]);
        this.mentionQuery.set(null);

        // A `[image:<url>]` the user typed or pasted BY HAND is still honored — it never had a
        // thumbnail, but it is a reference all the same, so it is unioned in rather than ignored.
        // `convertToModelMessages` (in the transport) turns a `file` part with an `image/*`
        // mediaType into real vision content; without it the url reaches the model as a bare string
        // it can pass around but never actually SEE.
        const files: FileUIPart[] = [...new Set([...attached, ...extractImageReferenceUrls(userMessage)])].map(
            (url) => ({
                type: 'file',
                mediaType: guessImageMediaType(url),
                url,
            })
        );

        this.history.beforeSend();
        await this.chat.sendMessage(files.length ? { text, files } : { text });
        this.changeDetectorRef.detectChanges();
    }

    public onMessageInputKeyDown(event: KeyboardEvent): void {
        if (this.mentionQuery() && this.mentionCandidates().length) {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.activeMentionIndex.update((index) => Math.min(index + 1, this.mentionCandidates().length - 1));
                    return;
                case 'ArrowUp':
                    event.preventDefault();
                    this.activeMentionIndex.update((index) => Math.max(index - 1, 0));
                    return;
                case 'Enter':
                case 'Tab':
                    event.preventDefault();
                    this.selectMentionCandidate(this.activeMentionCandidate());
                    return;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    this.closeMentionDropdown();
                    return;
                default:
                    break;
            }
        }

        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSendMessage();
        }
    }

    public messageText(message: SendpulseUIMessage, isStreaming = false): string {
        // Each step of the tool loop contributes its own text part — join them as paragraphs
        // (rendered via `white-space: pre-line`), not glued together ("…for you.Perfect!…").
        const text = message.parts
            .filter((part) => part.type === 'text')
            .map((part) => part.text.trim())
            .filter(Boolean)
            .join('\n\n');

        // An assistant turn can legitimately end with only tool calls and no prose (e.g. the
        // step cap cut the loop short) — the tool chips still show what ran, so only fall back
        // to a placeholder when there is nothing to show at all. While the message is still
        // streaming, the inline loading dots are the placeholder — no '...' needed.
        return message.role === 'assistant' && !text && !isStreaming && !this.messageToolCalls(message).length
            ? '...'
            : text;
    }

    /**
     * Tool calls executed during this assistant turn, for the ✓/⚠ chips under the message text.
     * A call counts as failed when it hard-errored OR when it "succeeded" but silently dropped
     * fields it didn't recognize (`ignored`) — previously surfaced as ⚠️ text appended to the
     * message, now carried by the tool part itself.
     */
    public messageToolCalls(message: SendpulseUIMessage): { name: string; failed: boolean }[] {
        return message.parts.filter(isToolUIPart).map((part) => {
            const output = part.state === 'output-available' ? (part.output as Partial<AiOperationResult>) : undefined;

            return {
                name: getToolName(part),
                failed: part.state === 'output-error' || output?.ok === false || Boolean(output?.ignored?.length),
            };
        });
    }
}
